/**
 * In-memory cache utility for API services
 */

import { logger } from '@/lib/utils/logger';
import { CACHE_DURATIONS, CACHE_SETTINGS, isCacheEnabled } from '@/lib/config/cache.config';
import { getRequestTenant } from '@/lib/api/api-client';
import { tenantFor } from '@/lib/tenant';

// Cache item interface
export interface CacheItem<T> {
  data: T;
  expiry: number;
  refreshing?: boolean;
  lastRefreshed?: number;
}

export class CacheService {
  // In-memory cache
  private static cache: Record<string, CacheItem<unknown>> = {};

  // Default cache duration from config
  private static DEFAULT_CACHE_DURATION = CACHE_DURATIONS.MEDIUM;

  // Refresh threshold from config
  private static REFRESH_THRESHOLD = CACHE_SETTINGS.REFRESH_THRESHOLD;

  // Refresh callbacks for auto-refreshing cache items
  private static refreshCallbacks: Record<string, () => Promise<unknown>> = {};

  /**
   * Builds a tenant-isolated cache key so multiple tenants sharing one
   * Next.js server process never read each other's cached CMS responses.
   */
  static async scopedKey(key: string): Promise<string> {
    if (typeof window !== 'undefined') {
      const clientTenant =
        tenantFor(window.location.host, window.location.pathname) ??
        (window.location.host.toLowerCase() || '__default__');
      return `${clientTenant}::${key}`;
    }
    const tenant = await getRequestTenant();
    return `${tenant ?? '__default__'}::${key}`;
  }

  /**
   * Cache'den veri alır
   * @param key Cache anahtarı
   * @returns Cache'deki veri veya null
   */
  static getFromCache<T>(key: string): T | null {
    const item = this.cache[key];

    if (!isCacheEnabled()) {
      logger.debug('Cache Service: cache disabled', { key });
      return null;
    }

    // Cache item yoksa veya süresi dolmuşsa null döndür
    if (!item || Date.now() > item.expiry) {
      if (item) {
        // Süresi dolmuş cache öğesini temizle
        delete this.cache[key];
        
        // Eğer bu anahtar için bir refresh callback varsa çağır
        this.triggerRefresh(key);
      }
      return null;
    }
    
    // Eğer cache süresi dolmak üzereyse ve yenilenmiyor durumdaysa, arka planda yenile
    const timeToExpiry = item.expiry - Date.now();
    if (timeToExpiry < this.REFRESH_THRESHOLD && !item.refreshing) {
      this.triggerRefresh(key);
    }

    return item.data as T;
  }

  /**
   * Veriyi cache'e ekler
   * @param key Cache anahtarı
   * @param data Cache'e eklenecek veri
   * @param duration Cache süresi (milisaniye cinsinden)
   */
  static addToCache<T>(key: string, data: T, duration: number = this.DEFAULT_CACHE_DURATION): void {
    this.cache[key] = {
      data,
      expiry: Date.now() + duration,
      lastRefreshed: Date.now(),
      refreshing: false
    };
  }

  /**
   * Cache'i temizler
   * @param key Temizlenecek cache anahtarı (boş ise tüm cache temizlenir)
   */
  static clearCache(key?: string): void {
    if (key) {
      delete this.cache[key];
      // Refresh callback'i de temizle
      delete this.refreshCallbacks[key];
    } else {
      this.cache = {};
      this.refreshCallbacks = {};
    }
  }
  
  /**
   * Cache için otomatik yenileme callback'i ekler
   * @param key Cache anahtarı
   * @param callback Yenileme işlemini gerçekleştirecek fonksiyon
   */
  static registerRefreshCallback(key: string, callback: () => Promise<unknown>): void {
    this.refreshCallbacks[key] = callback;
  }
  
  /**
   * Cache yenileme işlemini tetikler
   * @param key Cache anahtarı
   */
  private static async triggerRefresh(key: string): Promise<void> {
    const callback = this.refreshCallbacks[key];
    if (!callback) return;
    
    // Eğer cache öğesi varsa, refreshing flag'ini true yap
    if (this.cache[key]) {
      this.cache[key].refreshing = true;
    }
    
    try {
      // Callback'i çağır, sonucu bekleme (arka planda çalışacak)
      callback().catch(error => {
        logger.error(`Cache refresh error for key "${key}"`, error);
      });
    } catch (error) {
      logger.error(`Error triggering cache refresh for key "${key}"`, error);
    }
  }
}
